import { useTranslation } from 'react-i18next';
import { Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { CloneTenantRequest, PasswordSchema, Tenant } from '@bliks-io/contracts';
import { useCloneTenant } from '@/pages/admin/api/mutations/useTenants';
import { Alert, AlertDescription, AlertTitle, Label, PasswordInput, cn } from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';
import {
  TenantIdentityFields,
  TenantIdentityFormValues,
  dateInputToIso,
  isoToDateInput
} from './components/TenantIdentityFields';

interface CloneTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: Tenant;
}

/**
 * Form shape, not the contract shape: expiresAt holds a date-input string and
 * demoPassword stays optional here — the demo/password pairing lives in the
 * contract's discriminated union, built in toCloneRequest.
 */
type CloneTenantFormValues = TenantIdentityFormValues & { demoPassword?: string };

function toCloneRequest(data: CloneTenantFormValues): CloneTenantRequest {
  const identity = {
    name: data.name,
    slug: data.slug,
    expiresAt: dateInputToIso(data.expiresAt),
    description: data.description?.trim() || undefined
  };
  return data.tenantType === 'demo'
    ? { ...identity, tenantType: 'demo', demoPassword: data.demoPassword ?? '' }
    : { ...identity, tenantType: data.tenantType };
}

export function CloneTenantDialog({ open, onOpenChange, source }: CloneTenantDialogProps) {
  const { t } = useTranslation();
  const cloneTenant = useCloneTenant();

  // Clones default to a demo tenant (the common case — spinning up an isolated
  // demo from a real org), regardless of the source's type.
  const defaultValues: CloneTenantFormValues = {
    name: t('tenantAdmin.cloneNameDefault', { name: source.name }),
    slug: `${source.slug}-copy`,
    tenantType: 'demo',
    expiresAt: isoToDateInput(source.expiresAt),
    description: source.description || ''
  };

  const onSubmit = async (data: CloneTenantFormValues) => {
    await cloneTenant.mutateAsync({
      sourceId: source._id,
      data: toCloneRequest(data)
    });
    // 202: the copy runs detached, so there is nothing left to wait for here.
    // Progress and outcome are watched on the tenant list, which polls the
    // clone jobs. Everything that can fail synchronously — a taken slug above
    // all — still surfaces in this dialog's alert, next to the field the admin
    // has to change.
    toast.info(t('tenantAdmin.cloneStarted'));
    onOpenChange(false);
  };

  return (
    <FormDialog<CloneTenantFormValues>
      open={open}
      onOpenChange={onOpenChange}
      title={t('tenantAdmin.cloneTenant')}
      description={t('tenantAdmin.cloneDescription', { name: source.name })}
      icon={Copy}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={t('tenantAdmin.cloneTenant')}
      cancelLabel={t('tenantAdmin.cancel')}
      // `demoPassword` is off the list on purpose: it only renders while the
      // demo type is selected, so its server error belongs in the root alert.
      fields={['name', 'slug', 'tenantType', 'expiresAt', 'description']}
      size="md"
    >
      {({ form }) => {
        const isDemo = form.watch('tenantType') === 'demo';
        return (
          <div className="space-y-4">
            <Alert colorScheme="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {t(isDemo ? 'tenantAdmin.cloneWarningDemoTitle' : 'tenantAdmin.cloneWarningTitle')}
              </AlertTitle>
              <AlertDescription>
                {t(isDemo ? 'tenantAdmin.cloneWarningDemo' : 'tenantAdmin.cloneWarning')}
              </AlertDescription>
            </Alert>

            <TenantIdentityFields form={form} autoSlug />

            {isDemo && (
              <div className="space-y-2">
                <Label htmlFor="demoPassword">{t('tenantAdmin.demoPassword')}</Label>
                <PasswordInput
                  id="demoPassword"
                  autoComplete="new-password"
                  {...form.register('demoPassword', {
                    // Checked against form values: the rule survives unmounting
                    // (switching demo -> customer) and must not block then.
                    // PasswordSchema mirrors the Zitadel org policy, so a weak
                    // password fails here instead of deep in the import.
                    validate: (value, formValues) =>
                      formValues.tenantType !== 'demo' ||
                      PasswordSchema.safeParse(value).success ||
                      t('tenantAdmin.demoPasswordRequired')
                  })}
                  className={cn(form.formState.errors.demoPassword && 'border-destructive')}
                />
                {form.formState.errors.demoPassword && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.demoPassword.message}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">{t('tenantAdmin.demoPasswordHint')}</p>
              </div>
            )}
          </div>
        );
      }}
    </FormDialog>
  );
}
